"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPropertyByIdPublic } from "@/services/properties-api";
import type { NormalizedPropertyRecord } from "@/services/properties-api";

const PROPERTY_CACHE_KEY = "lien-vest:property:";
const PROPERTY_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export function getCachedProperty(
	propertyId: string
): NormalizedPropertyRecord | undefined {
	if (typeof window === "undefined") return undefined;
	try {
		const raw = sessionStorage.getItem(
			`${PROPERTY_CACHE_KEY}${propertyId}`
		);
		if (!raw) return undefined;
		const { data, at } = JSON.parse(raw) as {
			data: NormalizedPropertyRecord;
			at: number;
		};
		if (Date.now() - at > PROPERTY_CACHE_MAX_AGE_MS) return undefined;
		return data;
	} catch {
		return undefined;
	}
}

export function setCachedProperty(
	propertyId: string,
	data: NormalizedPropertyRecord
): void {
	try {
		sessionStorage.setItem(
			`${PROPERTY_CACHE_KEY}${propertyId}`,
			JSON.stringify({ data, at: Date.now() })
		);
	} catch {
		// ignore quota or other errors
	}
}

/** Shared query key so property and parcel pages share cache and never refetch once loaded. */
export const propertyQueryKey = (propertyId: string) =>
	["property", propertyId] as const;

/**
 * Fetches a single property by ID. Uses sessionStorage cache as initialData and
 * does not refetch when cache exists (enabled: false), so data stays stable after load.
 */
export function usePropertyById(propertyId: string) {
	const initialData = useMemo(
		() => getCachedProperty(propertyId),
		[propertyId]
	);

	return useQuery({
		queryKey: propertyQueryKey(propertyId),
		queryFn: async () => {
			const result = await getPropertyByIdPublic(propertyId);
			setCachedProperty(propertyId, result);
			return result;
		},
		initialData: initialData ?? undefined,
		initialDataUpdatedAt: initialData
			? Date.now() - PROPERTY_CACHE_MAX_AGE_MS
			: 0,
		// When we have cached data, don't run the query so it never refetches
		enabled: !initialData,
		retry: 2,
		staleTime: Infinity,
		gcTime: 60 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});
}
